import { IS_APP, IS_DEV, IS_TESTING } from "@/__main__/constants.mjs";
import { readData } from "@/__main__/get-data.mjs";
import {
  HUB_NAME_MAP,
  HUB_SYMBOL_GAME_GUIDE,
  HUB_SYMBOL_OVERLAYS,
  HUB_SYMBOL_SETTINGS,
} from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import gamesList from "@/app/games.mjs";
import { getRedirectUrl } from "@/app/util.mjs";
import {
  syncAppVersion,
  syncHardwareAcceleration,
  syncLatestMacRelease,
  syncLatestWindowsRelease,
  syncLoginItemSettings,
  syncMinimizeBlitzToTray,
} from "@/hub-settings/actions.mjs";
import settingsRefs from "@/hub-settings/refs.mjs";
import generalRefs from "@/routes/general.refs.mjs";
import { homepageRoutes } from "@/routes/marketing.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import { setSearchQuery } from "@/search/actions.mjs";
import { findGameSymbol } from "@/util/game-route.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import pathRegExp from "@/util/path-regexp.mjs";

const fetchValPrivacySetting = lazyFn(
  () => import("@/game-val/fetch-privacy-setting.mjs"),
);
const apexFetchOverlaysData = lazyFn(
  () => import("@/game-apex/apex-fetch-overlays-data.mjs"),
);

// These routes are deprecated.
const gameGuideRoute: BaseRoute = {
  path: `/${HUB_NAME_MAP[HUB_SYMBOL_GAME_GUIDE]}`,
  redirect: "/",
};
const destiny2Routes: BaseRoute = {
  path: /\/destiny2/,
  redirect: "/",
};

const overlaysRoute: BaseRoute = {
  path: pathRegExp(`/${HUB_NAME_MAP[HUB_SYMBOL_OVERLAYS]}`),
  hubSymbol: HUB_SYMBOL_OVERLAYS,
  component: "hub-overlays/Overlays.jsx",
  fetchData(...args) {
    // WRONG!!! do not fetch apex specific stuff for all overlays
    return apexFetchOverlaysData(...args);
  },
};

const settingsRoute: BaseRoute = {
  path: `/${HUB_NAME_MAP[HUB_SYMBOL_SETTINGS]}`,
  hubSymbol: HUB_SYMBOL_SETTINGS,
  component: "hub-settings/Settings.jsx",
  async fetchData() {
    const gs = findGameSymbol();
    const { settings } =
      gamesList.find(
        ({ gameSymbol }) => (gameSymbol as unknown as GameSymbol) === gs,
      ) ?? {};

    await Promise.all([
      syncLoginItemSettings(),
      syncHardwareAcceleration(),
      syncMinimizeBlitzToTray(),
      syncAppVersion(),
      syncLatestWindowsRelease(),
      syncLatestMacRelease(),
      fetchValPrivacySetting(), // WTF... doesn't belong here
      generalRefs.getActiveSessionsData(),
      generalRefs.fetchUserProInfo(),
      settings &&
        settings().then(({ default: Component }) => {
          settingsRefs.GameSettings = Component;
        }),
    ]);
  },
};

const initialAppRoute: BaseRoute = {
  path: "/",
  async redirect() {
    const [settings, localState] = await Promise.all([
      readData(["settings"]),
      readData(["localState"]),
    ]);

    // If user has any logged in accounts, redirect to profile
    const redirectUrl = getRedirectUrl(settings, localState);
    if (redirectUrl && redirectUrl !== "/") return redirectUrl;

    return "/getting-started";
  },
};

const gettingStartedRoute: BaseRoute = {
  path: "/getting-started",
  component: "app/GettingStarted.tsx",
};

const legalRoutes: BaseRoute[] = [
  // redirect routes from web
  {
    path: pathRegExp(
      `/:tab(privacy-policy|terms-of-service|blitz-battles-rules)`,
    ),
    redirect: ({ parameters: [tab] }) => `/legal/${tab}`,
  },
  {
    path: pathRegExp(
      `/legal/:tab(privacy-policy|terms-of-service|blitz-battles-rules)`,
    ),
    component: "app/Legal.tsx",
  },

  {
    path: "/blitz-arena-rules",
    redirect: `/legal/blitz-battles-rules`,
  },
  {
    path: "/legal/blitz-arena-rules",
    redirect: `/legal/blitz-battles-rules`,
  },
];

export const gameHubRoutes = gamesList.reduce((acc, definition) => {
  if (!definition.isActive) return acc;
  const { shortName, gameSymbol } = definition;
  acc[gameSymbol] = [
    ...[overlaysRoute, settingsRoute].map((route) => {
      return {
        ...route,
        path: `/${HUB_NAME_MAP[route.hubSymbol]}/${shortName}`,
      };
    }),
    {
      ...overlaysRoute,
      path: pathRegExp(
        `/${HUB_NAME_MAP[overlaysRoute.hubSymbol]}/${shortName}`,
      ),
    },
    IS_APP && {
      // trivial redirect game url to settings
      path: `/${shortName}`,
      redirect: `/settings/${shortName}`,
    },
  ].filter(Boolean);
  return acc;
}, {});

const routes: BaseRoute[] = [
  // Optional
  IS_APP && initialAppRoute,
  IS_APP && gettingStartedRoute,
  (IS_DEV || IS_TESTING) && {
    path: "/dev/video-ad-test",
    component: "app/VideoAdTestPage.tsx",
  },
  IS_DEV && {
    path: "/dev/colors",
    component: "app/ColorPage.tsx",
  },
  (IS_DEV || IS_TESTING) && {
    path: "/dev/test-form",
    component: "app/TestForm.tsx",
  },
  IS_DEV && {
    path: "/dev/performance",
    component: "app/dev-pages/Performance.tsx",
  },

  // Destiny 2
  destiny2Routes,

  // Hub
  gameGuideRoute,
  overlaysRoute,
  settingsRoute,

  // Search
  {
    path: pathRegExp("/search"),
    component: "search/SearchPage.tsx",
    fetchData: function (_p, searchParams, _state) {
      setSearchQuery(searchParams.get("q") ?? "");
    },
  },

  // Homepage
  ...homepageRoutes,

  // Static
  ...legalRoutes,
].filter(Boolean);

export default routes;
